import classnames from 'classnames'
import React from 'react'

import { Delayed } from '../../../../components/delayed'
import { useDialogState } from '../../../../components/dialog'
import { Spinner } from '../../../../components/spinner'
import { WIDGET_BOX_STYLES } from '../constants'
import { useETFHoldingsQuery, useRefetchData } from '../hooks'
import {
  ETFHoldingDataType,
  ETFHoldingsDialogType,
  ETFHoldingsQuery,
  ETFHoldingsStandardResponse,
  ETFHoldingsTopTenType,
  ETFHoldingsWidgetNavigation,
} from '../types'
import { getNormalizedStandardData } from '../utils'
import { Box } from './Box'
import { ETFHoldingsDialog } from './ETFHoldingsDialog'
import { EliteAdOverlay } from './EliteAdOverlay'
import { Header } from './Header'
import { StandardMap } from './StandardMap'
import { TableChart } from './Table'
import { TableFooter } from './TableFooter'

type TopTenViewProps = {
  query: ReturnType<typeof useETFHoldingsQuery>
}

const VIEW_SETTINGS: ETFHoldingsWidgetNavigation = {
  navType: ETFHoldingsQuery.TopTen,
  title: 'Top 10 Holdings',
  viewTypes: [
    { icon: 'chartTable', chartType: ETFHoldingsTopTenType.Table },
    { icon: 'chartTreemap', chartType: ETFHoldingsTopTenType.Map },
  ],
}

function getChartComponent({
  ticker,
  type,
  data,
  handleDialogToggle,
}: {
  ticker: string
  type: ETFHoldingsTopTenType
  data: ETFHoldingsStandardResponse | null
  handleDialogToggle: (value?: ETFHoldingsDialogType) => void
}) {
  if (!data || data.rowData.length === 0)
    return (
      <Delayed delayComponent={<div className="h-full w-full" />}>
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      </Delayed>
    )

  return type === ETFHoldingsTopTenType.Table ? (
    <>
      <TableChart type={type} data={data} limitDataTo={10} />
      <TableFooter type={type} ticker={ticker} data={data} handleDialogToggle={handleDialogToggle} />
    </>
  ) : (
    <StandardMap type={type} data={data} limitDataTo={10} handleDialogToggle={handleDialogToggle} />
  )
}

export function TopTenView({ query }: TopTenViewProps) {
  const isPremium = FinvizSettings.hasUserPremium
  const fetchedData = useRefetchData(query.ticker, ETFHoldingDataType.TopTen)
  const data = React.useMemo(() => getNormalizedStandardData(fetchedData.data), [fetchedData.data])
  const dialogState = useDialogState()
  const [isEliteOverlayVisible, setIsEliteOverlayVisible] = React.useState(false)
  const [dialogViewType, setDialogViewType] = React.useState<ETFHoldingsDialogType>()

  const handleDialogToggle = (value?: ETFHoldingsDialogType) => {
    if (isPremium) {
      if (value) {
        setDialogViewType(value)
        dialogState.show()
      } else {
        dialogState.hide()
      }
    } else {
      setIsEliteOverlayVisible(true)
    }
  }

  return (
    <Box key={VIEW_SETTINGS.navType} className={WIDGET_BOX_STYLES}>
      <div
        className={classnames('flex shrink grow flex-col', {
          'pointer-events-none blur-xs': VIEW_SETTINGS.navType === ETFHoldingsQuery.TopTen && isEliteOverlayVisible,
        })}
      >
        <Header query={query} widgetType={VIEW_SETTINGS} />
        {getChartComponent({ ticker: query.ticker, type: query.tt, data, handleDialogToggle })}
      </div>
      {VIEW_SETTINGS.navType === ETFHoldingsQuery.TopTen && isEliteOverlayVisible && (
        <EliteAdOverlay onOverlayClick={() => setIsEliteOverlayVisible(false)} />
      )}
      {isPremium && dialogViewType && (
        <ETFHoldingsDialog
          dialogState={dialogState}
          etfTicker={query.ticker}
          type={dialogViewType}
          handleDialogToggle={handleDialogToggle}
        />
      )}
    </Box>
  )
}
